import type {
  ActivityCalculationMethodContext,
  ActivityCalculationMethodOption,
  ActivityDataCalculationMethod,
} from '../types/activity-calculation-method';

import type { SelectOption } from '@/components/ui/select';
import type { TranslateFn } from '@/lib/i18n';

const CALCULATION_METHOD_EXAMPLE_UNITS: Record<ActivityDataCalculationMethod, string[]> = {
  supplier_specific: ['kg', 'kWh', 'l'],
  average_data: ['kg', 'l', 'item'],
  activity_based: ['kWh', 'kg', 'km'],
  spend_based: ['USD', 'EUR', 'PLN'],
  distance_based: ['km', 'mi', 'passenger_km'],
  fuel_based: ['l', 'kWh', 'kg'],
  location_based: ['kWh', 'MWh', 'GJ'],
  market_based: ['kWh', 'MWh', 'GJ'],
  mass_balance: ['kg', 't', 'lb'],
  process_specific: ['kg', 't', 'm3'],
  bdo_codes_based: ['kg', 't', 'lb'],
  employee_based: ['km', 'passenger_km', 'headcount'],
  site_specific: ['kWh', 'kg', 'm3'],
  hybrid: [],
};

const CALCULATION_METHOD_OPTIONS: ActivityCalculationMethodOption[] = [
  {
    value: 'supplier_specific',
    label: 'Supplier-specific',
    description: 'Primary emissions data or factors reported directly by the supplier.',
  },
  {
    value: 'average_data',
    label: 'Average-data',
    description: 'Secondary average factors applied to measured quantities of goods or services.',
  },
  {
    value: 'activity_based',
    label: 'Activity-based',
    description: 'Measured activity quantities such as energy, materials, waste, or nights stayed.',
  },
  {
    value: 'spend_based',
    label: 'Spend-based',
    description: 'Financial spend translated to emissions with monetary emission factors.',
  },
  {
    value: 'distance_based',
    label: 'Distance-based',
    description: 'Travel or freight emissions calculated from distance travelled.',
  },
  {
    value: 'fuel_based',
    label: 'Fuel-based',
    description: 'Fuel or energy quantity converted to emissions using combustion factors.',
  },
  {
    value: 'location_based',
    label: 'Location-based',
    description: 'Scope 2 electricity using average grid emission factors for the location.',
  },
  {
    value: 'market_based',
    label: 'Market-based',
    description: 'Scope 2 electricity using supplier-specific factors or contractual instruments.',
  },
  {
    value: 'mass_balance',
    label: 'Mass balance',
    description: 'Fugitive gas emissions based on refrigerant or gas mass movements.',
  },
  {
    value: 'process_specific',
    label: 'Process-specific',
    description: 'Process emissions calculated from process-specific material or output data.',
  },
  {
    value: 'bdo_codes_based',
    label: 'BDO codes-based',
    description: 'Waste emissions classified and calculated using BDO waste-code records.',
  },
  {
    value: 'employee_based',
    label: 'Employee-based',
    description: 'Employee survey or HR commuting and travel data aggregated per employee pattern.',
  },
  {
    value: 'site_specific',
    label: 'Site-specific (legacy)',
    description:
      'Legacy alias for older site-specific entries; mapped to the current category default.',
  },
];

const DISTANCE_BASED_ACTIVITY_METHODS = new Set([
  'Distance',
  'PassengerOverDistance',
  'WeightOverDistance',
  'ContainerOverDistance',
  'DistanceOverTime',
]);

const FUEL_BASED_ACTIVITY_METHODS = new Set(['Energy', 'Volume', 'Weight']);

const SITE_SPECIFIC_ACTIVITY_METHODS = new Set([
  'Area',
  'AreaOverTime',
  'Data',
  'DataOverTime',
  'Energy',
  'Number',
  'NumberOverTime',
  'Power',
  'Time',
  'Volume',
  'Weight',
  'WeightOverTime',
]);

const PROCESS_SPECIFIC_ACTIVITY_METHODS = new Set(['Energy', 'Number', 'Volume', 'Weight']);
const ENERGY_ACTIVITY_METHODS = new Set(['Energy']);
const MASS_BALANCE_ACTIVITY_METHODS = new Set(['Weight']);
const BDO_CODES_ACTIVITY_METHODS = new Set(['Weight']);
const EMPLOYEE_BASED_ACTIVITY_METHODS = new Set([
  ...DISTANCE_BASED_ACTIVITY_METHODS,
  'Number',
  'NumberOverTime',
]);
const PRODUCT_QUANTITY_ACTIVITY_METHODS = new Set(['Area', 'Number', 'Volume', 'Weight']);

const CATEGORY_DEFAULTS: Record<string, ActivityDataCalculationMethod> = {
  stationary_combustion: 'fuel_based',
  mobile_combustion: 'fuel_based',
  process_emissions: 'process_specific',
  fugitive_emissions: 'mass_balance',
  purchased_electricity: 'location_based',
  purchased_steam: 'activity_based',
  purchased_heating: 'activity_based',
  purchased_cooling: 'activity_based',
  purchased_goods: 'spend_based',
  purchased_goods_services: 'spend_based',
  capital_goods: 'spend_based',
  business_travel: 'distance_based',
  employee_commuting: 'distance_based',
  upstream_transportation: 'distance_based',
  downstream_transportation: 'distance_based',
  fuel_energy_activities: 'fuel_based',
  waste_operations: 'activity_based',
  upstream_leased_assets: 'activity_based',
  downstream_leased_assets: 'activity_based',
  processing_sold_products: 'average_data',
  use_sold_products: 'activity_based',
  end_of_life_sold_products: 'activity_based',
  franchises: 'activity_based',
  investments: 'spend_based',
};

const CATEGORY_ALLOWED_METHODS: Record<string, ActivityDataCalculationMethod[]> = {
  stationary_combustion: ['fuel_based'],
  mobile_combustion: ['fuel_based', 'distance_based'],
  process_emissions: ['process_specific'],
  fugitive_emissions: ['mass_balance'],
  purchased_electricity: ['location_based', 'market_based'],
  purchased_steam: ['activity_based', 'supplier_specific'],
  purchased_heating: ['activity_based', 'supplier_specific'],
  purchased_cooling: ['activity_based', 'supplier_specific'],
  purchased_goods: ['spend_based', 'average_data', 'activity_based', 'supplier_specific'],
  purchased_goods_services: ['spend_based', 'average_data', 'activity_based', 'supplier_specific'],
  capital_goods: ['spend_based', 'average_data', 'activity_based', 'supplier_specific'],
  business_travel: ['distance_based', 'spend_based', 'employee_based'],
  employee_commuting: ['distance_based', 'spend_based', 'employee_based'],
  upstream_transportation: ['distance_based'],
  downstream_transportation: ['distance_based'],
  fuel_energy_activities: ['fuel_based', 'activity_based', 'average_data', 'supplier_specific'],
  waste_operations: ['activity_based', 'bdo_codes_based', 'spend_based', 'supplier_specific'],
  upstream_leased_assets: ['activity_based', 'spend_based', 'supplier_specific'],
  downstream_leased_assets: ['activity_based', 'spend_based', 'supplier_specific'],
  processing_sold_products: ['average_data', 'spend_based', 'supplier_specific'],
  use_sold_products: ['activity_based', 'spend_based', 'average_data'],
  end_of_life_sold_products: ['activity_based', 'spend_based', 'average_data'],
  franchises: ['activity_based', 'spend_based'],
  investments: ['spend_based', 'average_data'],
};

const DEFAULT_ALLOWED_METHODS_SCOPE12: ActivityDataCalculationMethod[] = [
  'activity_based',
  'supplier_specific',
];

const DEFAULT_ALLOWED_METHODS_SCOPE3: ActivityDataCalculationMethod[] = [
  'average_data',
  'activity_based',
  'supplier_specific',
  'spend_based',
];

const CATEGORY_PRIMARY_ACTIVITY_METHODS: Record<string, Set<string>> = {
  stationary_combustion: new Set(FUEL_BASED_ACTIVITY_METHODS),
  mobile_combustion: new Set([...DISTANCE_BASED_ACTIVITY_METHODS, ...FUEL_BASED_ACTIVITY_METHODS]),
  process_emissions: new Set(PROCESS_SPECIFIC_ACTIVITY_METHODS),
  fugitive_emissions: new Set(MASS_BALANCE_ACTIVITY_METHODS),
  purchased_electricity: new Set(ENERGY_ACTIVITY_METHODS),
  purchased_steam: new Set(ENERGY_ACTIVITY_METHODS),
  purchased_heating: new Set(ENERGY_ACTIVITY_METHODS),
  purchased_cooling: new Set(ENERGY_ACTIVITY_METHODS),
  purchased_goods: new Set(PRODUCT_QUANTITY_ACTIVITY_METHODS),
  purchased_goods_services: new Set(PRODUCT_QUANTITY_ACTIVITY_METHODS),
  capital_goods: new Set(PRODUCT_QUANTITY_ACTIVITY_METHODS),
  business_travel: new Set(EMPLOYEE_BASED_ACTIVITY_METHODS),
  employee_commuting: new Set(EMPLOYEE_BASED_ACTIVITY_METHODS),
  upstream_transportation: new Set(['Distance', 'WeightOverDistance']),
  downstream_transportation: new Set(['Distance', 'WeightOverDistance']),
  fuel_energy_activities: new Set(FUEL_BASED_ACTIVITY_METHODS),
  waste_operations: new Set(['Number', 'Volume', 'Weight', 'WeightOverTime']),
  upstream_leased_assets: new Set([...ENERGY_ACTIVITY_METHODS, 'Area', 'AreaOverTime']),
  downstream_leased_assets: new Set([...ENERGY_ACTIVITY_METHODS, 'Area', 'AreaOverTime']),
  processing_sold_products: new Set(PRODUCT_QUANTITY_ACTIVITY_METHODS),
  use_sold_products: new Set([...ENERGY_ACTIVITY_METHODS, ...PRODUCT_QUANTITY_ACTIVITY_METHODS]),
  end_of_life_sold_products: new Set(['Number', 'Volume', 'Weight', 'WeightOverTime']),
  franchises: new Set([...ENERGY_ACTIVITY_METHODS, 'Area', 'AreaOverTime']),
  investments: new Set(PRODUCT_QUANTITY_ACTIVITY_METHODS),
};

const CURRENCY_UNITS = new Set([
  '$',
  'usd',
  'eur',
  'gbp',
  'cad',
  'aud',
  'jpy',
  'cny',
  'chf',
  'sek',
  'nok',
  'dkk',
  'pln',
]);

const DISTANCE_UNITS = new Set(['m', 'km', 'ft', 'mi', 'nmi', 'pkm', 'passenger_km']);
const WEIGHT_DISTANCE_UNITS = new Set(['tkm', 'ton_km', 'tonne_km']);
const ENERGY_UNITS = new Set([
  'wh',
  'kwh',
  'mwh',
  'gwh',
  'mj',
  'gj',
  'tj',
  'btu',
  'therm',
  'mmbtu',
]);
const VOLUME_UNITS = new Set(['ml', 'l', 'm3', 'gallon_us', 'bbl', 'cubic_foot']);
const WEIGHT_UNITS = new Set(['g', 'kg', 't', 'ton', 'lb', 'tonne']);

const UNIT_ALIASES: Record<string, string> = {
  liter: 'l',
  liters: 'l',
  litre: 'l',
  litres: 'l',
  gallon: 'gallon_us',
  gallons: 'gallon_us',
  us_gallon: 'gallon_us',
  us_gallons: 'gallon_us',
  cubic_feet: 'cubic_foot',
  cubic_foot: 'cubic_foot',
  cubic_meter: 'm3',
  cubic_meters: 'm3',
  cubic_metre: 'm3',
  cubic_metres: 'm3',
  tonnes: 'tonne',
  tons: 'ton',
  pounds: 'lb',
  kilograms: 'kg',
  grams: 'g',
  nights: 'night',
  stays: 'stay',
  room_nights: 'room_night',
  'room-night': 'room_night',
};

function normalizeCategory(activityCategory: string): string {
  return activityCategory.trim().toLowerCase();
}

function normalizeUnit(unit: string): string {
  const normalized = unit.trim().toLowerCase().replaceAll(' ', '_');
  return UNIT_ALIASES[normalized] ?? normalized;
}

function getPrimaryActivityMethods(context: ActivityCalculationMethodContext): Set<string> {
  const normalizedCategory = normalizeCategory(context.activityCategory);
  const categoryMethods = CATEGORY_PRIMARY_ACTIVITY_METHODS[normalizedCategory];
  if (categoryMethods) return new Set(categoryMethods);
  if (context.scope === 2) return new Set(ENERGY_ACTIVITY_METHODS);
  if (context.scope === 3)
    return new Set([...SITE_SPECIFIC_ACTIVITY_METHODS, ...FUEL_BASED_ACTIVITY_METHODS]);
  return new Set(SITE_SPECIFIC_ACTIVITY_METHODS);
}

function normalizeCalculationMethodForContext(
  context: ActivityCalculationMethodContext,
  method: ActivityDataCalculationMethod
): ActivityDataCalculationMethod {
  if (method !== 'site_specific') return method;
  const allowed = getAllowedCalculationMethodsForContext(context);
  if (allowed.includes(method)) return method;
  return getDefaultCalculationMethodForContext(context);
}

/**
 * Get label for a calculation method. When `t` is provided, returns a translated label.
 */
export function getActivityCalculationMethodLabel(
  method: ActivityDataCalculationMethod,
  t?: TranslateFn
): string {
  if (t) {
    return t(`ghg:calculationMethods.${method}.label`);
  }
  return CALCULATION_METHOD_OPTIONS.find((option) => option.value === method)?.label ?? method;
}

/**
 * Get description for a calculation method. When `t` is provided, returns a translated description.
 */
export function getActivityCalculationMethodDescription(
  method: ActivityDataCalculationMethod,
  t?: TranslateFn
): string {
  let description: string;
  if (t) {
    description = t(`ghg:calculationMethods.${method}.description`);
  } else {
    description =
      CALCULATION_METHOD_OPTIONS.find((option) => option.value === method)?.description ?? '';
  }
  const exampleUnits = CALCULATION_METHOD_EXAMPLE_UNITS[method];
  if (!description || !exampleUnits.length) return description;
  return `${description} Example units: ${exampleUnits.slice(0, 3).join(', ')}.`;
}

export function getDefaultCalculationMethodForContext({
  scope,
  activityCategory,
}: ActivityCalculationMethodContext): ActivityDataCalculationMethod {
  const normalizedCategory = normalizeCategory(activityCategory);
  return CATEGORY_DEFAULTS[normalizedCategory] ?? (scope === 3 ? 'average_data' : 'activity_based');
}

export function getAllowedCalculationMethodsForContext({
  scope,
  activityCategory,
}: ActivityCalculationMethodContext): ActivityDataCalculationMethod[] {
  const normalizedCategory = normalizeCategory(activityCategory);
  return (
    CATEGORY_ALLOWED_METHODS[normalizedCategory] ??
    (scope === 3 ? DEFAULT_ALLOWED_METHODS_SCOPE3 : DEFAULT_ALLOWED_METHODS_SCOPE12)
  );
}

/**
 * Hard per-category activity method restrictions. When a category is listed here,
 * the returned set is intersected with these methods regardless of calculation method.
 * Use sparingly — only for categories that must lock to specific unit types.
 */
const CATEGORY_ACTIVITY_METHOD_RESTRICTIONS: Record<string, Set<string>> = {
  upstream_transportation: new Set(['Distance', 'WeightOverDistance']),
  downstream_transportation: new Set(['Distance', 'WeightOverDistance']),
};

export function getAllowedActivityMethodsForCalculationMethod(
  context: ActivityCalculationMethodContext,
  calculationMethod: ActivityDataCalculationMethod
): Set<string> {
  const normalizedMethod = normalizeCalculationMethodForContext(context, calculationMethod);
  let methods: Set<string>;
  if (normalizedMethod === 'spend_based') methods = new Set(['Money']);
  else if (normalizedMethod === 'distance_based') methods = new Set(DISTANCE_BASED_ACTIVITY_METHODS);
  else if (normalizedMethod === 'fuel_based') methods = new Set(FUEL_BASED_ACTIVITY_METHODS);
  else if (normalizedMethod === 'location_based' || normalizedMethod === 'market_based') {
    methods = new Set(ENERGY_ACTIVITY_METHODS);
  } else if (normalizedMethod === 'mass_balance') methods = new Set(MASS_BALANCE_ACTIVITY_METHODS);
  else if (normalizedMethod === 'process_specific')
    methods = new Set(PROCESS_SPECIFIC_ACTIVITY_METHODS);
  else if (normalizedMethod === 'bdo_codes_based') methods = new Set(BDO_CODES_ACTIVITY_METHODS);
  else if (normalizedMethod === 'employee_based') methods = new Set(EMPLOYEE_BASED_ACTIVITY_METHODS);
  else methods = getPrimaryActivityMethods(context);

  const restriction =
    CATEGORY_ACTIVITY_METHOD_RESTRICTIONS[normalizeCategory(context.activityCategory)];
  if (restriction) {
    return new Set([...methods].filter((m) => restriction.has(m)));
  }
  return methods;
}

/**
 * Get calculation method options for a select dropdown. When `t` is provided, labels/descriptions are translated.
 */
export function getCalculationMethodOptions(
  context: ActivityCalculationMethodContext,
  t?: TranslateFn
): SelectOption[] {
  const allowed = new Set(getAllowedCalculationMethodsForContext(context));
  return CALCULATION_METHOD_OPTIONS.filter(
    (option) => option.value !== 'site_specific' && allowed.has(option.value)
  ).map((option) => ({
    value: option.value,
    label: t ? t(`ghg:calculationMethods.${option.value}.label`) : option.label,
    helperText: t ? t(`ghg:calculationMethods.${option.value}.description`) : option.description,
  }));
}

export function inferCompatibleActivityMethodsFromUnit(unit: string): Set<string> {
  const normalized = normalizeUnit(unit);
  if (!normalized) return new Set();
  if (normalized === 'night' || normalized === 'stay' || normalized === 'room_night') {
    return new Set(['Number']);
  }
  if (normalized === 'pkm' || normalized === 'passenger_km') {
    return new Set(['PassengerOverDistance']);
  }
  if (WEIGHT_DISTANCE_UNITS.has(normalized)) {
    return new Set(['WeightOverDistance']);
  }
  if (CURRENCY_UNITS.has(normalized)) {
    return new Set(['Money']);
  }
  if (DISTANCE_UNITS.has(normalized)) {
    return new Set(['Distance', 'DistanceOverTime']);
  }
  if (ENERGY_UNITS.has(normalized)) {
    return new Set(['Energy']);
  }
  if (VOLUME_UNITS.has(normalized)) {
    return new Set(['Volume']);
  }
  if (WEIGHT_UNITS.has(normalized)) {
    return new Set(['Weight', 'WeightOverDistance', 'WeightOverTime']);
  }
  return new Set();
}

function inferCandidateCalculationMethodsForEntry(input: {
  scope: number;
  activityCategory: string;
  activityMethod: string;
  unit?: string | null;
}): Set<ActivityDataCalculationMethod> {
  const context = {
    scope: input.scope as ActivityCalculationMethodContext['scope'],
    activityCategory: input.activityCategory,
  };
  const normalizedMethod = input.activityMethod.trim();
  if (!normalizedMethod) return new Set();
  const normalizedUnit = input.unit?.trim() ? input.unit : null;

  return new Set(
    getAllowedCalculationMethodsForContext(context).filter((method) => {
      const allowedActivityMethods = getAllowedActivityMethodsForCalculationMethod(context, method);
      if (!allowedActivityMethods.has(normalizedMethod)) return false;
      if (!normalizedUnit) return true;
      return isCalculationMethodCompatible({
        scope: input.scope,
        activityCategory: input.activityCategory,
        calculationMethod: method,
        activityMethod: normalizedMethod,
        unit: normalizedUnit,
      });
    })
  );
}

export function inferCalculationMethodForEntry(input: {
  scope: number;
  activityCategory: string;
  activityMethod: string;
  unit?: string | null;
}): ActivityDataCalculationMethod | null {
  const candidates = inferCandidateCalculationMethodsForEntry(input);
  if (candidates.size === 1) {
    return [...candidates][0] ?? null;
  }
  return null;
}

export function isCalculationMethodCompatible(input: {
  scope: number;
  activityCategory: string;
  calculationMethod: ActivityDataCalculationMethod;
  activityMethod: string;
  unit?: string | null;
}): boolean {
  const context = {
    scope: input.scope as ActivityCalculationMethodContext['scope'],
    activityCategory: input.activityCategory,
  };
  const normalizedCalculationMethod = normalizeCalculationMethodForContext(
    context,
    input.calculationMethod
  );
  const allowedCalculationMethods = new Set(getAllowedCalculationMethodsForContext(context));
  if (!allowedCalculationMethods.has(normalizedCalculationMethod)) return false;

  const allowedActivityMethods = getAllowedActivityMethodsForCalculationMethod(
    context,
    normalizedCalculationMethod
  );
  if (!allowedActivityMethods.has(input.activityMethod.trim())) return false;

  if (!input.unit) return true;

  const compatibleActivityMethods = inferCompatibleActivityMethodsFromUnit(input.unit);
  if (compatibleActivityMethods.size === 0) return false;
  return [...compatibleActivityMethods].some((method) => allowedActivityMethods.has(method));
}
